/**
 * Utility functions for copying styles to Picture-in-Picture windows.
 * Handles styled-components, dynamic CSS, and cross-origin stylesheets.
 */

import Logger from 'meet-hour-logger';

const logger = Logger.getLogger(__filename);

/**
 * Detects non-critical external stylesheet loading errors.
 *
 * @param {Error} error - The thrown stylesheet error.
 * @returns {boolean}
 */
function isExpectedExternalStyleError(error) {
    const message = `${error?.message || ''}`;

    return /Timeout loading/i.test(message)
        && /(fonts\.googleapis\.com|fonts\.gstatic\.com)/i.test(message);
}

/**
 * Whether a stylesheet URL belongs to the application: served from our own origin, or the Google Fonts stylesheet the
 * conference page links. Anything else in document.styleSheets was injected by a browser extension (password
 * managers, translators...) and styles only that extension's own elements, which are never inside the PiP window.
 * Such a sheet usually cannot even be loaded from the PiP document - e.g.
 * "Failed to load https://pwm-image.trendmicro.com/.../engineV3.css" - so it is not copied.
 *
 * @param {string} href - The stylesheet URL.
 * @returns {boolean}
 */
function isApplicationStylesheet(href) {
    try {
        const url = new URL(href, window.location.href);

        return url.origin === window.location.origin
            || /^fonts\.(googleapis|gstatic)\.com$/i.test(url.hostname);
    } catch {
        return false;
    }
}

/**
 * Copies all styles from the main document to a Picture-in-Picture window.
 * This includes:
 * - Inline style tags (including styled-components)
 * - External stylesheets (with cross-origin handling)
 * - Dynamic CSS rules from CSSStyleSheet objects
 * - Computed styles for critical elements
 *
 * @param {Window} pipWindow - The Picture-in-Picture window object.
 * @param {Object} options - Configuration options.
 * @param {boolean} options.copyFonts - Whether to copy font-face rules (default: true).
 * @param {boolean} options.copyAnimations - Whether to copy animation keyframes (default: true).
 * @param {boolean} options.addFallbackStyles - Whether to add fallback CSS (default: true).
 * @param {Function} options.onError - Error callback function.
 * @returns {Promise<void>}
 */
export async function copyStylesToPipWindow(pipWindow, options = {}) {
    const {
        copyFonts = true,
        copyAnimations = true,
        addFallbackStyles = true,
        onError = error => {
            if (isExpectedExternalStyleError(error)) {
                logger.warn('Style copy warning:', error);

                return;
            }

            logger.error('Style copy error:', error);
        }
    } = options;

    if (!pipWindow || !pipWindow.document) {
        logger.error('Invalid PiP window provided');

        return;
    }

    try {
        // Step 1: Copy all style tags (includes styled-components dynamic styles)
        await copyStyleTags(pipWindow);

        // Step 2: Copy external stylesheets and their rules
        await copyStyleSheets(pipWindow, { copyFonts,
            copyAnimations,
            onError });

        // Step 3: Add fallback styles for common UI elements
        if (addFallbackStyles) {
            addFallbackCss(pipWindow);
        }

        // Step 4: Ensure proper viewport and base styles
        ensureBaseStyles(pipWindow);

        logger.log('Successfully copied all styles to PiP window');
    } catch (error) {
        onError(error);

        if (isExpectedExternalStyleError(error)) {
            logger.warn('Failed to copy some optional external PiP styles:', error);
        } else {
            logger.error('Failed to copy styles to PiP window:', error);
        }
    }
}

/**
 * Copies all inline style tags from the main document to PiP window.
 * This captures styled-components and other dynamically injected styles.
 *
 * @param {Window} pipWindow - The Picture-in-Picture window.
 * @private
 */
function copyStyleTags(pipWindow) {
    const styleTags = document.querySelectorAll('style');

    styleTags.forEach((styleTag, index) => {
        try {
            const clonedStyle = styleTag.cloneNode(true);

            // Preserve data attributes (styled-components uses these)
            Array.from(styleTag.attributes).forEach(attr => {
                clonedStyle.setAttribute(attr.name, attr.value);
            });

            // Add identifier for debugging
            clonedStyle.setAttribute('data-pip-source', 'inline-style');
            clonedStyle.setAttribute('data-pip-index', index);

            pipWindow.document.head.appendChild(clonedStyle);
        } catch (error) {
            logger.warn(`Failed to copy style tag ${index}:`, error);
        }
    });

    logger.log(`Copied ${styleTags.length} inline style tags`);
}

/**
 * Copies external stylesheets and their CSS rules to PiP window.
 * Handles both accessible and cross-origin stylesheets.
 *
 * @param {Window} pipWindow - The Picture-in-Picture window.
 * @param {Object} options - Copy options.
 * @private
 */
async function copyStyleSheets(pipWindow, options) {
    const { copyFonts, copyAnimations, onError } = options;
    const styleSheets = Array.from(document.styleSheets);
    let successCount = 0;
    let failCount = 0;

    for (const styleSheet of styleSheets) {
        try {
            // Try to access cssRules to check if stylesheet is accessible
            const rules = Array.from(styleSheet.cssRules || []);

            if (rules.length > 0) {
                // Stylesheet is accessible - copy rules directly
                const cssText = rules
                    .filter(rule => {
                        // Filter rules based on options
                        if (!copyFonts && rule.type === CSSRule.FONT_FACE_RULE) {
                            return false;
                        }
                        if (!copyAnimations && rule.type === CSSRule.KEYFRAMES_RULE) {
                            return false;
                        }

                        return true;
                    })
                    .map(rule => rule.cssText)
                    .join('\n');

                const style = pipWindow.document.createElement('style');

                style.textContent = cssText;
                style.setAttribute('data-pip-source', 'stylesheet-rules');
                style.setAttribute('data-pip-href', styleSheet.href || 'inline');
                pipWindow.document.head.appendChild(style);
                successCount++;
            } else if (styleSheet.href && isApplicationStylesheet(styleSheet.href)) {
                // Empty or inaccessible stylesheet with href - create link element
                await copyExternalStylesheet(pipWindow, styleSheet);
                successCount++;
            }
        } catch (error) {
            // Cross-origin or restricted stylesheet - try to load via link tag
            if (styleSheet.href && !isApplicationStylesheet(styleSheet.href)) {
                continue;
            } else if (styleSheet.href) {
                try {
                    await copyExternalStylesheet(pipWindow, styleSheet);
                    successCount++;
                } catch (linkError) {
                    failCount++;
                    onError(linkError);
                    logger.warn(`Failed to copy stylesheet ${styleSheet.href}:`, linkError);
                }
            } else {
                failCount++;
                logger.warn('Failed to access inline stylesheet rules:', error);
            }
        }
    }

    logger.log(`Copied ${successCount} stylesheets, ${failCount} failed`);
}

/**
 * Copies an external stylesheet to PiP window using a link element.
 *
 * @param {Window} pipWindow - The Picture-in-Picture window.
 * @param {CSSStyleSheet} styleSheet - The stylesheet to copy.
 * @returns {Promise<void>}
 * @private
 */
function copyExternalStylesheet(pipWindow, styleSheet) {
    return new Promise((resolve, reject) => {
        const link = pipWindow.document.createElement('link');

        link.rel = 'stylesheet';
        link.type = styleSheet.type || 'text/css';
        link.media = styleSheet.media?.mediaText || 'all';
        link.href = styleSheet.href;
        link.setAttribute('data-pip-source', 'external-link');

        link.onload = () => resolve();
        link.onerror = () => reject(new Error(`Failed to load ${styleSheet.href}`));

        // Set timeout to prevent hanging
        const timeout = setTimeout(() => {
            reject(new Error(`Timeout loading ${styleSheet.href}`));
        }, 5000);

        link.onload = () => {
            clearTimeout(timeout);
            resolve();
        };

        pipWindow.document.head.appendChild(link);
    });
}

/**
 * Adds fallback CSS for common UI elements that might lose styling.
 * This ensures basic usability even if some styles fail to load.
 *
 * @param {Window} pipWindow - The Picture-in-Picture window.
 * @private
 */
function addFallbackCss(pipWindow) {
    const fallbackCss = `
        /* Fallback styles for PiP window */
        
        /* Reset and base styles */
        * {
            box-sizing: border-box;
        }
        
        body {
            margin: 0;
            padding: 0;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 
                         'Helvetica Neue', Arial, sans-serif;
            font-size: 14px;
            line-height: 1.5;
            color: #333;
            background: #000;
        }
        
        /* Checkbox styling */
        input[type="checkbox"] {
            width: 16px;
            height: 16px;
            cursor: pointer;
            accent-color: #0056b3;
        }
        
        /* Button styling */
        button {
            cursor: pointer;
            border: none;
            background: transparent;
            padding: 8px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
        }
        
        button:hover {
            opacity: 0.8;
        }
        
        button:active {
            opacity: 0.6;
        }
        
        /* SVG icon styling */
        svg {
            display: block;
            width: 1em;
            height: 1em;
            fill: currentColor;
            color: inherit;
        }
        
        /* Label styling */
        label {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            cursor: pointer;
            user-select: none;
        }
        
        /* Ensure proper color inheritance for icons */
        [class*="icon"],
        [class*="Icon"],
        .toolbox-icon,
        .toolbar-icon {
            color: inherit;
        }
        
        /* Video container */
        video {
            width: 100%;
            height: 100%;
            object-fit: contain;
        }
        
        /* Flex utilities */
        .flex {
            display: flex;
        }
        
        .flex-center {
            display: flex;
            align-items: center;
            justify-content: center;
        }
        
        /* Common spacing */
        .p-1 { padding: 4px; }
        .p-2 { padding: 8px; }
        .p-3 { padding: 12px; }
        .m-1 { margin: 4px; }
        .m-2 { margin: 8px; }
        .m-3 { margin: 12px; }
    `;

    const style = pipWindow.document.createElement('style');

    style.textContent = fallbackCss;
    style.setAttribute('data-pip-source', 'fallback-styles');
    pipWindow.document.head.appendChild(style);

    logger.log('Added fallback CSS to PiP window');
}

/**
 * Ensures base styles and viewport settings are properly configured.
 *
 * @param {Window} pipWindow - The Picture-in-Picture window.
 * @private
 */
function ensureBaseStyles(pipWindow) {
    // Add viewport meta tag if not present
    if (!pipWindow.document.querySelector('meta[name="viewport"]')) {
        const viewport = pipWindow.document.createElement('meta');

        viewport.name = 'viewport';
        viewport.content = 'width=device-width, initial-scale=1.0, user-scalable=0';
        pipWindow.document.head.appendChild(viewport);
    }

    // Add charset meta tag if not present
    if (!pipWindow.document.querySelector('meta[charset]')) {
        const charset = pipWindow.document.createElement('meta');

        charset.setAttribute('charset', 'UTF-8');
        pipWindow.document.head.insertBefore(charset, pipWindow.document.head.firstChild);
    }

    // Set document title
    pipWindow.document.title = document.title || 'Picture in Picture';
}

/**
 * Watches for style changes in the main document and syncs them to PiP window.
 * Useful for applications that dynamically inject styles after initial load.
 *
 * @param {Window} pipWindow - The Picture-in-Picture window.
 * @param {Object} options - Watch options.
 * @param {number} options.debounceMs - Debounce time in milliseconds (default: 100).
 * @returns {Function} Cleanup function to stop watching.
 */
export function watchStyleChanges(pipWindow, options = {}) {
    const { debounceMs = 100 } = options;
    let timeoutId = null;

    const syncStyles = () => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }

        timeoutId = setTimeout(() => {
            copyStylesToPipWindow(pipWindow, { addFallbackStyles: false });
        }, debounceMs);
    };

    // Watch for new style tags being added
    const observer = new MutationObserver(mutations => {
        const hasStyleChanges = mutations.some(mutation =>
            Array.from(mutation.addedNodes).some(node =>
                node.nodeName === 'STYLE' || node.nodeName === 'LINK'
            )
        );

        if (hasStyleChanges) {
            syncStyles();
        }
    });

    observer.observe(document.head, {
        childList: true,
        subtree: true
    });

    logger.log('Started watching for style changes');

    // Return cleanup function
    return () => {
        observer.disconnect();
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        logger.log('Stopped watching for style changes');
    };
}

/**
 * Copies computed styles from specific elements to ensure they render correctly in PiP.
 * Useful for elements that rely heavily on inherited styles.
 *
 * @param {Window} pipWindow - The Picture-in-Picture window.
 * @param {string[]} selectors - Array of CSS selectors to copy computed styles from.
 */
export function copyComputedStyles(pipWindow, selectors = []) {
    const computedStylesRules = [];

    selectors.forEach(selector => {
        try {
            const elements = document.querySelectorAll(selector);

            elements.forEach((element, index) => {
                const computed = window.getComputedStyle(element);
                const importantProps = [
                    'color',
                    'background-color',
                    'font-family',
                    'font-size',
                    'font-weight',
                    'line-height',
                    'display',
                    'flex-direction',
                    'align-items',
                    'justify-content'
                ];

                const rules = importantProps
                    .map(prop => `${prop}: ${computed.getPropertyValue(prop)};`)
                    .join(' ');

                computedStylesRules.push(`${selector}:nth-of-type(${index + 1}) { ${rules} }`);
            });
        } catch (error) {
            logger.warn(`Failed to copy computed styles for ${selector}:`, error);
        }
    });

    if (computedStylesRules.length > 0) {
        const style = pipWindow.document.createElement('style');

        style.textContent = computedStylesRules.join('\n');
        style.setAttribute('data-pip-source', 'computed-styles');
        pipWindow.document.head.appendChild(style);

        logger.log(`Copied computed styles for ${selectors.length} selectors`);
    }
}
